import { FC, PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';

import { ReactComponent as PlusCircleIcon } from '@/assets/icons/violet-plus-circle.svg';
import Text from '@/components/Text';

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  & + & {
    margin-top: 24px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Heading = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const IndexBadge = styled.span`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.purple};
  color: ${({ theme }) => theme.colors.grayscaleLight};
  font-family: ${({ theme }) => theme.typography.extraSmall.fontFamily};
  font-size: ${({ theme }) => theme.typography.extraSmall.fontSize};
  font-weight: ${({ theme }) => theme.typography.extraSmall.fontWeight};
  line-height: ${({ theme }) => theme.typography.extraSmall.lineHeight};
`;

const RemoveButton = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.error};
  font-family: ${({ theme }) => theme.typography.smallHeadline.fontFamily};
  font-size: ${({ theme }) => theme.typography.smallHeadline.fontSize};
  font-weight: ${({ theme }) => theme.typography.smallHeadline.fontWeight};
  line-height: ${({ theme }) => theme.typography.smallHeadline.lineHeight};
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 8px;
`;

/** Side-by-side fields inside a card; every child takes an equal share. */
export const FieldRow = styled.div`
  display: flex;
  gap: 16px;

  > * {
    flex: 1;
    min-width: 0;
  }
`;

const AddAnother = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: ${({ theme }) => theme.typography.smallHeadline.fontFamily};
  font-size: ${({ theme }) => theme.typography.smallHeadline.fontSize};
  font-weight: ${({ theme }) => theme.typography.smallHeadline.fontWeight};
  line-height: ${({ theme }) => theme.typography.smallHeadline.lineHeight};
  color: ${({ theme }) => theme.colors.black};
`;

interface AddAnotherButtonProps {
  onClick: () => void;
}

/** "Add another …" text link with the violet plus icon. */
export const AddAnotherButton: FC<PropsWithChildren<AddAnotherButtonProps>> = ({
  onClick,
  children,
}) => (
  <AddAnother type="button" onClick={onClick}>
    <PlusCircleIcon />
    {children}
  </AddAnother>
);

interface Props {
  /** Heading text, e.g. "Amount" — the numbered badge is appended to it. */
  label: ReactNode;
  index: number;
  /** Numbering only makes sense once there are several groups. */
  showIndex?: boolean;
  /** Omit to hide the Remove link (e.g. when a single group is left). */
  onRemove?: () => void;
}

/** One repeatable amount/bonus block: numbered heading above a bordered card. */
const InvoiceFieldGroup: FC<PropsWithChildren<Props>> = ({
  label,
  index,
  showIndex = true,
  onRemove,
  children,
}) => (
  <Group>
    <Header>
      <Heading>
        <Text as="span" $type="smallBold">
          {label}
        </Text>
        {showIndex && <IndexBadge>{index + 1}</IndexBadge>}
      </Heading>
      {onRemove && (
        <RemoveButton type="button" onClick={onRemove}>
          Remove
        </RemoveButton>
      )}
    </Header>
    <Card>{children}</Card>
  </Group>
);

export default InvoiceFieldGroup;
